import React, { useState } from "react";

import { InfoSmIcon as InfoSm, Typography } from "@packages/ds";
import { useIntl } from "react-intl";

import Error from "Components/Error";
import { CLI_COMMAND_NAME } from "Constants/documentationUrls";
import { Button, ButtonWrapper, IconControl } from "ds/Button";
import Checkbox from "ds/Checkbox";
import Dialog from "ds/Dialog";
import Modal from "ds/Modal";
import { Tooltip } from "ds/Tooltip";
import { capitalize, goToEnvironment } from "Libs/utils";
import {
  environmentSelector,
  environmentLoadingSelector
} from "Reducers/environment";
import {
  selectIsSynchronisationLoading,
  selectSynchronisationErrors,
  sync
} from "Reducers/environment/actions/sync";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import * as S from "./EnvironmentSyncPane.styles";

import type { NavigateFunction } from "react-router-dom";

type EnvironmentSyncPaneProps = {
  push: NavigateFunction;
  organizationId: string;
  projectId: string;
  environmentId: string;
  isSizingApiEnabled?: boolean;
};

const EnvironmentSyncPane = ({
  push,
  organizationId,
  projectId,
  environmentId,
  isSizingApiEnabled
}: EnvironmentSyncPaneProps) => {
  const environment = useAppSelector(state =>
    environmentSelector(state, {
      organizationId,
      projectId,
      environmentId
    })
  );
  const parent = useAppSelector(state =>
    environmentSelector(state, {
      organizationId,
      projectId,
      environmentId: environment?.parent ?? undefined
    })
  );
  const isEnvironmentLoading = useAppSelector(state =>
    environmentLoadingSelector(state, {
      projectId,
      environmentId
    })
  );
  const isLoading = useAppSelector(selectIsSynchronisationLoading);
  const errors = useAppSelector(selectSynchronisationErrors);

  const dispatch = useAppDispatch();

  const handleSync = async (
    syncData: boolean,
    syncCode: boolean,
    syncResources?: boolean
  ) => {
    const response = await dispatch(
      sync({
        organizationId,
        projectId,
        environmentId,
        options: {
          syncData,
          syncCode,
          syncResources
        }
      })
    );

    if (response.meta.requestStatus === "fulfilled")
      push(
        `/${organizationId}/${projectId}/${encodeURIComponent(environmentId)}`
      );
  };

  const intl = useIntl();

  const [syncCode, setSyncCode] = useState(false);
  const [syncData, setSyncData] = useState(false);
  const [syncResources, setSyncResources] = useState<boolean>();

  const toggleSyncData = () => {
    setSyncData(prev => !prev);
  };

  const toggleSyncCode = () => {
    setSyncCode(prev => !prev);
  };

  const toggleSyncResources = () => {
    setSyncResources(prev => !prev);
  };

  const isOptionSelected = syncData || syncCode || syncResources;

  if (!isLoading && !parent) {
    return (
      <Typography.BodyText>
        {intl.formatMessage({
          id: "sync.dialog.cli.requirement",
          defaultMessage: "To sync an env need to have a parent"
        })}
      </Typography.BodyText>
    );
  }

  const command = isOptionSelected
    ? `${CLI_COMMAND_NAME} sync -e ${environmentId} ${(
        [
          [syncData, "data"],
          [syncCode, "code"],
          [syncResources, "resources"]
        ] as const
      )
        .filter(opt => opt[0])
        .map(opt => opt[1])
        .join(" ")}`
    : "";

  const checkboxLabelData = (
    <S.SimpleBodyText>
      <S.CheckboxWrapper>
        {isSizingApiEnabled ? (
          <>
            {intl.formatMessage({ id: "sync.data.sizing_api_enabled" })}
            <Dialog
              side="bottom"
              align="center"
              analyticId="sync.data.dialog"
              title={intl.formatMessage({ id: "sync.replace_data" })}
              description={intl.formatMessage(
                { id: "sync.data.dialog" },
                {
                  currentEnvironment: environment?.title
                }
              )}
              link={
                <Button
                  analyticId="learnmore"
                  href={intl.formatMessage({
                    id: "links.documentation.manage_resources.environment.sync"
                  })}
                  variant="link"
                >
                  {intl.formatMessage({
                    id: "learnmore"
                  })}
                </Button>
              }
              trigger={
                <IconControl>
                  <InfoSm />
                </IconControl>
              }
            />
          </>
        ) : (
          intl.formatMessage(
            { id: "sync.data" },
            {
              parentEnvironment: parent?.title,
              currentEnvironment: environment?.title
            }
          )
        )}
      </S.CheckboxWrapper>
    </S.SimpleBodyText>
  );

  const checkboxLabelCode = (
    <S.SimpleBodyText>
      <S.CheckboxWrapper>
        {intl.formatMessage(
          { id: "sync.code" },
          {
            parentEnvironment: parent?.title,
            currentEnvironment: environment?.title
          }
        )}
      </S.CheckboxWrapper>
    </S.SimpleBodyText>
  );

  const checkboxLabelResources = (
    <S.SimpleBodyText>
      <S.CheckboxWrapper>
        {intl.formatMessage(
          { id: "sync.resources" },
          {
            parentEnvironment: parent?.title,
            currentEnvironment: environment?.title
          }
        )}
        <Dialog
          side="bottom"
          align="center"
          analyticId="sync.resources.dialog"
          title={intl.formatMessage({ id: "sync.sync_resources" })}
          description={intl.formatMessage({ id: "sync.resources.dialog" })}
          link={
            <Button
              href={intl.formatMessage({
                id: "links.documentation.manage_resources.environment.sync"
              })}
              analyticId="learnmore"
              variant="link"
            >
              {intl.formatMessage({
                id: "learnmore"
              })}
            </Button>
          }
          trigger={
            <IconControl>
              <InfoSm />
            </IconControl>
          }
        />
      </S.CheckboxWrapper>
    </S.SimpleBodyText>
  );

  const cliText = intl.formatMessage({
    id: isSizingApiEnabled ? "sync.cli.sizing_api_enabled" : "sync.cli"
  });

  const syncBtn = (
    <Button
      analyticId="sync.button"
      id="environment-sync-save-btn"
      type="submit"
      onClick={e => {
        e.preventDefault();
        handleSync(syncData, syncCode, syncResources);
      }}
      disabled={!isOptionSelected}
    >
      {capitalize(intl.formatMessage({ id: "sync.button" }))}
    </Button>
  );

  const successBtn = isOptionSelected ? (
    syncBtn
  ) : (
    <Tooltip
      side="top"
      align="end"
      tooltip={intl.formatMessage({ id: "sync.select" })}
      analyticId="sync.select"
    >
      {syncBtn}
    </Tooltip>
  );

  const onClose = (): void => {
    goToEnvironment(push, organizationId, projectId, environmentId);
  };

  return (
    <Modal
      isOpen={true}
      size="medium"
      onOpenChange={onClose}
      handleClose={onClose}
    >
      <div>
        <Typography.Title tag="h3" id="environment-sync-modal-title">
          {intl.formatMessage(
            { id: "sync.title" },
            {
              parentEnvironment: parent?.title,
              currentEnvironment: environment?.title
            }
          )}
        </Typography.Title>
        {typeof isSizingApiEnabled === "undefined" ? (
          <S.Loading />
        ) : (
          <div>
            {errors && <Error>{errors}</Error>}
            <S.ModalDescription>
              {intl.formatMessage(
                { id: "sync.description" },
                {
                  parentEnvironment: (
                    <S.EnvironmentNameWrapper>
                      {parent?.title}
                    </S.EnvironmentNameWrapper>
                  ),
                  currentEnvironment: (
                    <S.EnvironmentNameWrapper>
                      {environment?.title}
                    </S.EnvironmentNameWrapper>
                  )
                }
              )}
            </S.ModalDescription>
            <div>
              <Checkbox
                id="environment-sync-modal-data-switch"
                forId="environment-sync-modal-data"
                label={checkboxLabelData}
                onCheckedChanged={toggleSyncData}
                checked={syncData}
              />
              <Checkbox
                id="environment-sync-modal-code-switch"
                forId="environment-sync-modal-code"
                label={checkboxLabelCode}
                onCheckedChanged={toggleSyncCode}
                checked={syncCode}
              />
              {isSizingApiEnabled && (
                <Checkbox
                  id="environment-sync-modal-resources-switch"
                  forId="environment-sync-modal-resources"
                  label={checkboxLabelResources}
                  onCheckedChanged={toggleSyncResources}
                  checked={syncResources ?? false}
                />
              )}
            </div>
            <S.Separator />
            <S.DialogWrapper>
              <Typography.Label textStyle="default">{cliText}</Typography.Label>

              <Dialog
                side="right"
                align="center"
                analyticId="sync.dialog.cli.description"
                title={intl.formatMessage({ id: "learnmore" })}
                description={intl.formatMessage(
                  { id: "sync.dialog.cli.description" },
                  {
                    currentEnvironment: environment?.title,
                    parentEnvironment: parent?.title
                  }
                )}
                link={
                  <Button
                    href={intl.formatMessage({
                      id: "links.documentation.help.CLI"
                    })}
                    analyticId="help_text"
                    variant="link"
                  >
                    {intl.formatMessage({
                      id: "help_text",
                      defaultMessage: "Help"
                    })}
                  </Button>
                }
                trigger={
                  <IconControl>
                    <InfoSm />
                  </IconControl>
                }
              />
            </S.DialogWrapper>

            <S.CodeBlock isCopyable singleLine id="environment-sync-cmd">
              {command}
            </S.CodeBlock>

            {isEnvironmentLoading || isLoading ? (
              <S.LoadingSuccess />
            ) : (
              <ButtonWrapper spacing="modal" justifyContent="end">
                <Button
                  analyticId="cancel"
                  id="environment-sync-cancel-btn"
                  variant="secondary"
                  onClick={onClose}
                >
                  {capitalize(intl.formatMessage({ id: "cancel" }))}
                </Button>

                {successBtn}
              </ButtonWrapper>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default EnvironmentSyncPane;
